import { TabContext } from '@mui/lab';
import { Box } from '@mui/material';
import { SyntheticEvent, useEffect, useState } from 'react';

import { ImplantationInsertion } from '../../../api/characteristics/enumerations';
import {
  CompletionData,
  HouseCharacteristicsData,
  SectionStatus,
} from '../../../api/characteristics/types';
import {
  SectionIndexAutocomplete,
  SectionIndexItem,
  scrollToId,
} from '../components/SectionIndexAutocomplete';
import { StyledTab, StyledTabList, StyledTabPanel } from '../styles';
import { HouseConstructionAreasSection } from './constructionAreas';
import { HouseEnterpriseSection } from './enterprise';
import { HouseEvaluatingUnitSection } from './evaluatingUnit';
import { HouseFramesAndFacadesSection } from './framesAndFacades';
import { HouseLandAndFencingSection } from './landAndFencing';
import { HouseRegionSection } from './region';
import { houseSectionIndex } from './sectionIndex';
import { HouseWetAndDryAreasSection } from './wetAndDryAreas';

interface HouseCharacteristicsProps {
  characteristicsData: HouseCharacteristicsData;
  completionData?: CompletionData;
}

const baseTabs: { key: keyof HouseCharacteristicsData; label: string }[] = [
  { key: 'region', label: 'Características e Região' },
  { key: 'evaluating_unit', label: 'Unidade Avaliando' },
  { key: 'frames_and_facades', label: 'Esquadrias e Fachadas' },
  { key: 'wet_and_dry_areas', label: 'Áreas Molhadas e Secas' },
  { key: 'construction_areas', label: 'Áreas de Construção' },
  { key: 'land_and_fencing', label: 'Terreno e Cercamento' },
];

export function HouseCharacteristics({
  characteristicsData,
  completionData,
}: HouseCharacteristicsProps): JSX.Element {
  const sectionStatus = (
    key: keyof HouseCharacteristicsData
  ): SectionStatus | undefined => completionData?.sections[key]?.status;
  const isCondominium =
    characteristicsData.evaluating_unit.position_and_insertion
      .implantation_insertion === ImplantationInsertion.CONDOMINIUM;

  const tabs = isCondominium
    ? [
        ...baseTabs,
        {
          key: 'enterprise' as keyof HouseCharacteristicsData,
          label: 'Empreendimento',
        },
      ]
    : baseTabs;

  const [activeTab, setActiveTab] =
    useState<keyof HouseCharacteristicsData>('region');
  const [scrollTarget, setScrollTarget] = useState<string | null>(null);

  useEffect(() => {
    let timeout: ReturnType<typeof setTimeout>;
    if (scrollTarget) {
      timeout = setTimeout(() => {
        scrollToId(scrollTarget);
        setScrollTarget(null);
      }, 150);
    }
    return () => clearTimeout(timeout);
  }, [scrollTarget, activeTab]);

  const handleTabChange = (
    _: SyntheticEvent,
    newTab: keyof HouseCharacteristicsData
  ): void => {
    setActiveTab(newTab);
  };

  const handleSearchNavigate = (item: SectionIndexItem): void => {
    if (item.tabKey === activeTab) {
      scrollToId(item.sectionId);
      return;
    }
    setActiveTab(item.tabKey as keyof HouseCharacteristicsData);
    setScrollTarget(item.sectionId);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      <SectionIndexAutocomplete
        options={houseSectionIndex}
        onNavigate={handleSearchNavigate}
      />
      <TabContext value={activeTab}>
        <StyledTabList
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            '& .MuiTabs-scrollButtons.Mui-disabled': {
              display: 'none',
            },
          }}
        >
          {tabs.map(({ key, label }) => (
            <StyledTab key={key} value={key} label={label} />
          ))}
        </StyledTabList>
        <StyledTabPanel value={activeTab}>
          {activeTab === 'region' && (
            <HouseRegionSection
              formData={characteristicsData.region}
              sectionStatus={sectionStatus('region')}
            />
          )}
          {activeTab === 'evaluating_unit' && (
            <HouseEvaluatingUnitSection
              formData={characteristicsData.evaluating_unit}
              sectionStatus={sectionStatus('evaluating_unit')}
            />
          )}
          {activeTab === 'frames_and_facades' && (
            <HouseFramesAndFacadesSection
              formData={characteristicsData.frames_and_facades}
              sectionStatus={sectionStatus('frames_and_facades')}
            />
          )}
          {activeTab === 'wet_and_dry_areas' && (
            <HouseWetAndDryAreasSection
              formData={characteristicsData.wet_and_dry_areas}
              sectionStatus={sectionStatus('wet_and_dry_areas')}
            />
          )}
          {activeTab === 'construction_areas' && (
            <HouseConstructionAreasSection
              formData={characteristicsData.construction_areas}
              sectionStatus={sectionStatus('construction_areas')}
            />
          )}
          {activeTab === 'land_and_fencing' && (
            <HouseLandAndFencingSection
              formData={characteristicsData.land_and_fencing}
              sectionStatus={sectionStatus('land_and_fencing')}
            />
          )}
          {activeTab === 'enterprise' && characteristicsData.enterprise && (
            <HouseEnterpriseSection
              formData={characteristicsData.enterprise}
              sectionStatus={sectionStatus('enterprise')}
            />
          )}
        </StyledTabPanel>
      </TabContext>
    </Box>
  );
}
